/** @file Utilities for the tenant usage report page */

import type { Data } from '@/components/Chart/types';
import { formatNumber } from '@/util';

/**
 * Build the estimated-usage chart overlay from the report payload's parallel
 * arrays. Report payloads are baked (encrypted) at billing-send time, so reports
 * generated before estimates shipped carry no estimated fields; this returns []
 * for those and the chart renders exactly as before.
 *
 * Days with zero estimated volume are skipped so the overlay only draws where an
 * estimate actually applies.
 */
export const buildReportEstimatedData = (
  dateArray: string[],
  estimatedVolumes: number[] | undefined,
  estimatedReasons: (string | null)[] | undefined,
): Data => {
  if (!estimatedVolumes) {
    return [];
  }

  return dateArray
    .map((date, index) => {
      const volume = estimatedVolumes[index] ?? 0;
      const reason = estimatedReasons?.[index] ?? null;
      return {
        date,
        volume,
        gallons: volume,
        label: `${formatNumber(volume)} gallons estimated on ${date}${
          reason !== null ? `\nReason: ${reason}` : ''
        }`,
      };
    })
    .filter((entry) => entry.volume > 0);
};
